/**
 * Apply the library's AUTO APPLY lexicons the first time a document is opened
 * (VE-24948 / U5).
 *
 * The counterpart to `watermarkAutoApply`, and it inherits that saga's two hard
 * rules because they were learned on a path that writes without being asked:
 *
 * FIRST OPEN ONLY. "First" means the file has NEVER BEEN SAVED — `lastSavedAt`
 * is null until somebody saves one. (Not simply "until an asset exists": the
 * upload path stamps one for the lexicons picked in the upload dialog, and
 * `openDocument` excludes that one by its `writtenBy` marker. Counting it was
 * review finding B4, and it killed auto-apply for both features on the exact
 * open they run.) A document saved even once is never auto-applied to again,
 * whatever the library says. That is not a nicety here, it is what makes
 * `REMOVE_LEXICON_FROM_DOCUMENT` mean anything: without it, a reviewer who took
 * an auto-apply lexicon back off would find it returned on their next open, and
 * nothing they did could stop it.
 *
 * SILENT ON FAILURE. `loadAutoApplyLexiconIds` returns an empty list rather
 * than throwing, and this does nothing with an empty list. A registry blip must
 * leave the document exactly as it would have been, not raise an error about
 * something the reviewer never asked for.
 *
 * `source: 'auto'` on the record, which is the honest value: nobody pressed
 * anything. `appliedBy` still names the session's user, because they are who
 * the document was opened by and the trail has to name somebody — but the
 * source is what says they did not choose it, and a reviewer reading the
 * applied list can tell the two apart.
 *
 * It does NOT wait for the lexicon list the way the watermark saga waits for
 * its library: the ids are fetched here, by a call that owns its own transport,
 * so there is no in-flight fetch to race with.
 */
import { call, put, select, takeLatest } from 'typed-redux-saga/macro';
import { isNil } from 'lodash';

import {
  APPLY_LEXICONS_TO_OPEN_DOCUMENT,
  OPEN_DOCUMENT_SUCCESS,
} from '../actions';
import { selectSession } from '../selectors';
import { loadAutoApplyLexiconIds } from '@redact-modules/lexicons';
import { LEXICON_AUTO_APPLY_ENABLED } from '@redact-modules/lexicons/autoApplyConfig';

export function* autoApplyLexiconsOnOpen(
  action: ReturnType<typeof OPEN_DOCUMENT_SUCCESS>
) {
  // The feature is off, and this is the half that MATTERS being off. Hiding
  // the settings column alone would leave a flag nobody can see still applying
  // lexicons to documents on open — observable only as a document that changed
  // for no reason anyone can point at. See `autoApplyConfig` for why it is
  // off: it competes with the upload picker, and neither names the other.
  if (!LEXICON_AUTO_APPLY_ENABLED) {
    return;
  }
  // Saved before — the document's own `appliedLexicons` are the answer, and a
  // removal the reviewer made is part of that answer.
  if (!isNil(action.payload.lastSavedAt)) {
    return;
  }
  const session = yield* select(selectSession);
  if (!session) {
    return;
  }
  const ids = yield* call(loadAutoApplyLexiconIds);
  if (ids.length === 0) {
    return;
  }
  // Anything the upload selector already attached is skipped: that record
  // exists and the first-open evaluation (BR-50) is about to run it, so
  // applying it again here would be two runs of the same lexicon racing on one
  // document.
  const already = new Set(session.appliedLexicons.map((r) => r.lexiconId));
  const pending = ids.filter((id) => !already.has(id));
  if (pending.length === 0) {
    return;
  }
  yield* put(
    APPLY_LEXICONS_TO_OPEN_DOCUMENT({ lexiconIds: pending, source: 'auto' })
  );
}

export default function* lexiconAutoApplySaga() {
  yield* takeLatest(OPEN_DOCUMENT_SUCCESS, autoApplyLexiconsOnOpen);
}
